/**
 * Assets, the pure part: what fits where, how a file is named and counted, and how the grid of true shapes is laid
 * out. The Assets page and the control every upload opens both read it.
 */
import type { Attachment, LibraryAsset, LibraryFileKind, LibraryFolder, LibraryUse } from "../../shared/types";

// ── what fits where ─────────────────────────────────────────────────────────────

export interface Fit {
  /** "Only pictures": what the place takes, for the fit bar. Empty for a place that takes anything. */
  label: string;
  hint: string;
  /** The device picker's accept list. */
  accept: string;
  /** The folder the control opens on. */
  at: string;
  test: (a: LibraryAsset) => boolean;
  /** Why one file does not fit, said in the side pane. */
  why?: (a: LibraryAsset) => string;
}

/** A room's talk is ten minutes or more. */
const TALK_MIN_SEC = 600;

export const FITS = {
  talk: {
    label: "full talks",
    hint: "10 minutes or longer.",
    accept: "video/*,.mp4,.mov,.m4v",
    at: "all",
    test: (a) => a.kind === "video" && a.source !== "room" && (a.duration_sec == null ? a.ready === false : a.duration_sec >= TALK_MIN_SEC),
    why: (a) => (a.source === "room" ? "It’s already a room’s replay." : a.kind !== "video" ? "A room plays a video." : "It’s shorter than 10 minutes."),
  },
  video: { label: "videos", hint: "", accept: "video/*,.mp4,.mov,.m4v", at: "all", test: (a) => a.kind === "video" },
  handout: { label: "PDFs and pictures", hint: "", accept: ".pdf,image/*", at: "handouts", test: (a) => a.kind === "pdf" || a.kind === "image" },
  notes: {
    label: "PDFs and text files",
    hint: "",
    accept: ".pdf,.txt,.doc,.docx,.ppt,.pptx,.key",
    at: "all",
    test: (a) => a.kind === "pdf" || a.kind === "doc",
  },
  image: { label: "pictures", hint: "", accept: "image/*", at: "images", test: (a) => a.kind === "image" },
  media: { label: "pictures and videos", hint: "", accept: "image/*,video/*", at: "all", test: (a) => a.kind === "image" || a.kind === "video" },
  docs: {
    label: "PDFs and documents",
    hint: "",
    accept: ".pdf,.txt,.doc,.docx,.ppt,.pptx,.key",
    at: "handouts",
    test: (a) => a.kind === "pdf" || a.kind === "doc",
  },
  any: { label: "", hint: "", accept: "", at: "all", test: () => true },
  // Why a replay is left out: it plays in its room, and a message carries files, not rooms. A note or a guest's file is a file.
  send: { label: "", hint: "", accept: "", at: "all", test: (a) => a.source !== "room" || !a.video_id },
} satisfies Record<string, Fit>;

export type FitName = keyof typeof FITS;

export function fitOf(name: FitName | Fit | undefined): Fit {
  if (!name) return FITS.any;
  return typeof name === "string" ? FITS[name] : name;
}

// ── small words ────────────────────────────────────────────────────────────────

const pad = (n: number) => String(n).padStart(2, "0");

/** 1:24, or 1:02:05 past an hour. */
export function lenLabel(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s >= 3600) return `${Math.floor(s / 3600)}:${pad(Math.floor(s / 60) % 60)}:${pad(s % 60)}`;
  return `${Math.floor(s / 60)}:${pad(s % 60)}`;
}

export function bytesLabel(b: number): string {
  if (b >= 1e9) return `${(b / 1e9).toFixed(1)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(b / 1e3))} KB`;
}

export function extOf(name: string): string {
  return (/\.([a-z0-9]{2,5})$/i.exec(name)?.[1] ?? "").toUpperCase();
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** When it was added: "Just now", "12 min ago", "Today", "Yesterday", then "Mon 2 Nov". */
export function agoLabel(createdAt: number, now = Date.now()): string {
  const then = new Date(createdAt * 1000);
  const mins = (now - then.getTime()) / 60000;
  if (mins < 1) return "Just now";
  if (mins < 60) return `${Math.round(mins)} min ago`;
  const today = new Date(now);
  if (sameDay(then, today)) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(then, yesterday)) return "Yesterday";
  return `${then.toLocaleDateString("en-GB", { weekday: "short" })} ${then.getDate()} ${then.toLocaleDateString("en-GB", { month: "short" })}`;
}

const SLIDES = new Set(["PPT", "PPTX", "KEY"]);

/** What a file is, in a word: "Picture", "Full talk", "Replay", "Slides". */
export function kindName(a: Pick<LibraryAsset, "kind" | "name" | "source" | "duration_sec">): string {
  if (a.kind === "video") return a.source === "room" ? "Replay" : (a.duration_sec ?? 0) >= TALK_MIN_SEC ? "Full talk" : "Video";
  if (a.kind === "doc") return SLIDES.has(extOf(a.name)) ? "Slides" : "Text file";
  return { image: "Picture", pdf: "PDF", sound: "Sound" }[a.kind];
}

/**
 * Whether Flashroom's own copy of a file is there for a place that needs it: a picture, PDF or document by its key; a
 * video or sound by its note, and for `ready` playable too. A room's replay is its own and always is.
 */
export function hasCopy(a: LibraryAsset, wait: "whole" | "ready"): boolean {
  if (a.source === "room") return true;
  if (a.kind === "video" || a.kind === "sound") return wait === "whole" ? !!a.note_id : a.ready && !!a.media_key;
  return !!a.key;
}

/** A file's kind guessed from the device's file, before the server has seen it. */
export function guessKind(file: { name: string; type: string }): LibraryFileKind {
  const n = file.name.toLowerCase();
  const t = file.type || "";
  if (t.startsWith("video/") || /\.(mp4|mov|m4v|webm)$/.test(n)) return "video";
  if (t.startsWith("image/") || /\.(jpe?g|png|webp|gif|heic)$/.test(n)) return "image";
  if (t.startsWith("audio/") || /\.(mp3|wav|m4a|aac)$/.test(n)) return "sound";
  if (n.endsWith(".pdf") || t === "application/pdf") return "pdf";
  return "doc";
}

/**
 * What a message or a post keeps of a picked file: a picture or a file by its stored key, a video or a sound by its
 * note's keys. Null for what cannot ride along: a room's replay, or a video still being made playable.
 */
export function attachmentOf(a: LibraryAsset): Attachment | null {
  const size = a.size_bytes > 0 ? bytesLabel(a.size_bytes) : undefined;
  if (a.kind === "video" || a.kind === "sound") {
    if (!a.media_key) return null;
    const att: Attachment = { kind: a.kind === "video" ? "video" : "voice", name: a.name, media_key: a.media_key };
    if (size) att.size = size;
    if (a.poster_key) att.poster_key = a.poster_key;
    if (a.duration_sec) att.duration = lenLabel(a.duration_sec);
    return att;
  }
  if (!a.key) return null;
  return { kind: a.kind === "image" ? "image" : "file", name: a.name, key: a.key, ...(size ? { size } : {}) };
}

/**
 * Where Download goes. A room's replay, a note and a guest's file are Flashroom's own and play from a stream, so
 * Download asks for the original (signed, saved under its own name); every other file's address is its file.
 */
export function downloadHref(a: Pick<LibraryAsset, "id" | "source" | "url">): string | null {
  return a.source === "room" ? `/api/library/assets/${encodeURIComponent(a.id)}/download` : a.url;
}

// ── where it is used ───────────────────────────────────────────────────────────

interface UseCounts {
  rooms: number;
  emails: number;
  chats: number;
  pages: number;
  projects: number;
  all: boolean;
}

export function countUses(uses: LibraryUse[]): UseCounts {
  const rooms = new Set(uses.filter((u) => u.kind === "room" || u.kind === "offer").map((u) => u.ref)).size;
  return {
    rooms,
    emails: uses.filter((u) => u.kind === "email").length,
    chats: uses.filter((u) => u.kind === "chat").length,
    pages: uses.filter((u) => u.kind === "post").length,
    projects: uses.filter((u) => u.kind === "flashkut").length,
    all: uses.some((u) => u.kind === "all"),
  };
}

/** "In 2 rooms and an email", "In every room and email", or "Not used yet". */
export function usedLine(uses: LibraryUse[]): string {
  const r = countUses(uses);
  if (r.all) return "In every room and email";
  const bits: string[] = [];
  if (r.rooms) bits.push(`${r.rooms} room${r.rooms === 1 ? "" : "s"}`);
  if (r.emails) bits.push(r.emails === 1 ? "an email" : `${r.emails} emails`);
  if (r.chats) bits.push(r.chats === 1 ? "a chat" : `${r.chats} chats`);
  if (r.pages) bits.push(r.pages === 1 ? "their page" : `${r.pages} people’s pages`);
  if (r.projects) bits.push(r.projects === 1 ? "a Flashkut project" : `${r.projects} Flashkut projects`);
  if (!bits.length) return "Not used yet";
  return `In ${bits.length > 1 ? `${bits.slice(0, -1).join(", ")} and ${bits[bits.length - 1]}` : bits[0]}`;
}

function usedTotal(uses: LibraryUse[]): number {
  const r = countUses(uses);
  return r.all ? 99 : r.rooms + r.emails + r.chats + r.pages + r.projects;
}

// ── Trash ──────────────────────────────────────────────────────────────────────

/** The library keeps a deleted file this long. */
const KEEP_DAYS = 30;

export const TRASH_NOTE = "Files stay here 30 days, then they're deleted for good.";

/** Whole days a deleted file has left in Trash, never fewer than one. */
export function daysLeft(deletedAt: number, nowMs = Date.now()): number {
  return Math.max(1, KEEP_DAYS - Math.floor((nowMs - deletedAt * 1000) / 86_400_000));
}

const LIBRARY_PREFIX = "library/";

/**
 * The folder a deleted file was in. The folder itself may be gone from the list (it was deleted with the file), but a
 * file's id is its library path, so the name is still known; only a file that was never in a folder says Assets.
 */
export function trashFolderName(a: Pick<LibraryAsset, "id" | "folder_id">, folders: LibraryFolder[]): string {
  const live = folderOf(folders, a.folder_id);
  if (live) return live.name;
  const dir = a.id.startsWith(LIBRARY_PREFIX) ? a.id.slice(LIBRARY_PREFIX.length, Math.max(LIBRARY_PREFIX.length, a.id.lastIndexOf("/"))) : "";
  return dir ? dir.slice(dir.lastIndexOf("/") + 1) : "Assets";
}

const projectsWords = (n: number) => `${n} project${n === 1 ? "" : "s"}`;

/**
 * What a tile in Trash says: while a Flashkut project uses the file, "In 2 projects · Kept while used" (the library
 * keeps its bytes then); else how long is left and where it was. Uses in a project are only passed for a business with
 * Flashkut (`visibleAssets`), so anyone else reads the days.
 */
export function trashLine(a: Pick<LibraryAsset, "id" | "uses" | "deleted_at" | "folder_id">, folders: LibraryFolder[], nowMs = Date.now()): string {
  const projects = a.uses.filter((u) => u.kind === "flashkut").length;
  if (projects) return `In ${projectsWords(projects)} · Kept while used`;
  const left = daysLeft(a.deleted_at ?? Math.floor(nowMs / 1000), nowMs);
  return `${left} ${left === 1 ? "day" : "days"} left · From ${trashFolderName(a, folders)}`;
}

/** What the ones left out of "A, B and 3 more" are, in a word: all one thing, or "places" when they are mixed. */
const MORE_NOUN: Record<LibraryUse["kind"], string> = { flashkut: "project", room: "room", offer: "room", chat: "chat", email: "email", post: "page", all: "place" };

function moreNoun(uses: LibraryUse[], n: number): string {
  const kinds = new Set(uses.map((u) => MORE_NOUN[u.kind]));
  const noun = kinds.size === 1 ? [...kinds][0] : "place";
  return `${noun}${n === 1 ? "" : "s"}`;
}

/**
 * Who uses a file, as the words of a dialog: "Spring and Priya", "A, B and 2 more projects". Flashkut's words name its
 * projects; here a use can be a room, a chat, an email or a page too, so the noun follows what the rest are.
 */
function namesOf(uses: LibraryUse[]): string {
  const names = [...new Set(uses.map((u) => u.label))];
  if (names.length <= 2) return names.join(" and ");
  const first = names.slice(0, 2);
  const more = names.length - 2;
  return `${first.join(", ")} and ${more} more ${moreNoun(uses.filter((u) => !first.includes(u.label)), more)}`;
}

/** What Delete says of what is using the file or the files, and that they keep it: "X and Y use a file in it. They keep it." */
export function usedWords(uses: LibraryUse[], what: "it" | "a file in it"): string {
  if (!uses.length) return "";
  const names = [...new Set(uses.map((u) => u.label))];
  return names.length === 1 ? `${namesOf(uses)} uses ${what}. It keeps it.` : `${namesOf(uses)} use ${what}. They keep it.`;
}

/** What a restore says: where each file went, and which got a new name because another had its name there. */
export function restoreWords(restored: Array<{ was: string; asset: LibraryAsset }>, gone: string[], folders: LibraryFolder[]): string {
  // Flashkut's words: one name, two names, then the first two and how many more.
  const goneLine =
    gone.length === 1
      ? `${gone[0]} was already deleted for good.`
      : gone.length === 2
        ? `${gone[0]} and ${gone[1]} were already deleted for good.`
        : gone.length
          ? `${gone.slice(0, 2).join(", ")} and ${gone.length - 2} more were already deleted for good.`
          : "";
  if (!restored.length) return goneLine;
  const renamed = restored.filter((r) => r.asset.name !== r.was);
  let line: string;
  const first = restored[0] as { was: string; asset: LibraryAsset };
  if (restored.length === 1) {
    const at = folderName(folders, first.asset.folder_id);
    line = renamed.length ? `Restored ${first.was} to ${at} as ${first.asset.name}.` : `Restored ${first.asset.name} to ${at}.`;
  } else {
    const named = renamed.length ? ` ${renamed.length === 1 ? "1 got a new name" : `${renamed.length} got new names`}: ${renamed.map((r) => r.asset.name).join(", ")}.` : "";
    line = `Restored ${restored.length} files.${named}`;
  }
  return goneLine ? `${line} ${goneLine}` : line;
}

// ── folders ────────────────────────────────────────────────────────────────────

export const ALL = "all";

/** The rail's product groups: one folder per Flash project, and one per recorded room. */
export type ProductGroup = "Flashkut" | "Flashroom";

/** Which product group a folder belongs to; the library's own "made-in-flashkut" root is no folder of anyone's. */
export function groupOfFolder(f: Pick<LibraryFolder, "system" | "parent_id">): ProductGroup | null {
  if (f.system === "recordings") return "Flashroom";
  return f.system === "flashkut" && f.parent_id !== null ? "Flashkut" : null;
}

export function groupOfAsset(a: Pick<LibraryAsset, "source">): ProductGroup | null {
  return a.source === "flashkut" ? "Flashkut" : a.source === "room" ? "Flashroom" : null;
}

/** A group's own folders: a project each, a room each, and Flashroom's notes and guests (whose folders are kids). */
export const groupFolders = (folders: LibraryFolder[], g: ProductGroup): LibraryFolder[] => folders.filter((f) => groupOfFolder(f) === g && (g === "Flashkut" || f.parent_id === null));

/** The folders inside a Flashroom folder: one per guest, inside "Sent by guests". */
export const groupKids = (folders: LibraryFolder[], id: string): LibraryFolder[] => folders.filter((f) => groupOfFolder(f) === "Flashroom" && f.parent_id === id);

/** The person's own folders: no product's. */
export const ownFolders = (folders: LibraryFolder[]): LibraryFolder[] => folders.filter((f) => !f.system);

/** Where a search result is, in words: "In Handouts", or the product's way, "Flashroom › Weekly market update". */
export function searchPath(folders: LibraryFolder[], a: Pick<LibraryAsset, "source" | "folder_id">): string {
  const g = groupOfAsset(a);
  return g ? `${g} › ${folderName(folders, a.folder_id)}` : `In ${folderName(folders, a.folder_id)}`;
}

function folderOf(folders: LibraryFolder[], id: string | null | undefined): LibraryFolder | null {
  return id ? (folders.find((f) => f.id === id) ?? null) : null;
}

export function folderName(folders: LibraryFolder[], id: string | null | undefined): string {
  if (!id || id === ALL) return "Assets";
  return folderOf(folders, id)?.name ?? "Assets";
}

/** The folders from the top down to `id`, for the path. */
export function folderPath(folders: LibraryFolder[], id: string | null | undefined): LibraryFolder[] {
  const out: LibraryFolder[] = [];
  let f = folderOf(folders, id);
  const seen = new Set<string>();
  while (f && !seen.has(f.id)) {
    seen.add(f.id);
    out.unshift(f);
    f = f.parent_id ? folderOf(folders, f.parent_id) : null;
  }
  return out;
}

export function childFolders(folders: LibraryFolder[], id: string): LibraryFolder[] {
  if (id === ALL) return [];
  return folders.filter((f) => f.parent_id === id);
}

/** Product files are kept out of All files and the kinds: they live in their group's folders and in search. */
const inAll = (a: LibraryAsset) => groupOfAsset(a) === null;

function isUnder(folders: LibraryFolder[], a: LibraryAsset, id: string): boolean {
  return a.folder_id === id || folderPath(folders, a.folder_id).some((f) => f.id === id);
}

/** Where an upload lands: the open folder, unless it is one the app keeps itself; then Assets itself. */
export function uploadTarget(folders: LibraryFolder[], open: string): string | null {
  const f = folderOf(folders, open);
  return !f || f.system ? null : f.id;
}

/** The folders a business sees: Flash's project folder only when it has Flashkut. */
export function visibleFolders(folders: LibraryFolder[], flashkut: boolean): LibraryFolder[] {
  return flashkut ? folders : folders.filter((f) => f.system !== "flashkut");
}

/**
 * The files a business sees. Without Flashkut the name is nowhere: what Flash made in a project is left out, and so is
 * a file's use in a project, so no tile, search, count or inspector reads it.
 */
export function visibleAssets(assets: LibraryAsset[], flashkut: boolean): LibraryAsset[] {
  if (flashkut) return assets;
  return assets
    .filter((a) => a.source !== "flashkut")
    .map((a) => (a.uses.some((u) => u.kind === "flashkut") ? { ...a, uses: a.uses.filter((u) => u.kind !== "flashkut") } : a));
}

/**
 * Where a folder the person had open is after a folder was renamed or moved. A folder's id is its path, so a change
 * to a folder changes the id of everything inside it too.
 */
export function remapFolderId(open: string, from: string, to: string): string {
  if (open === from) return to;
  return open.startsWith(`${from}/`) ? `${to}${open.slice(from.length)}` : open;
}

/** The folders a folder can move into: not the app's own, not itself, not inside itself, not where it already is. */
export function moveTargets(folders: LibraryFolder[], id: string): LibraryFolder[] {
  const here = folderOf(folders, id)?.parent_id ?? null;
  return folders.filter((f) => !f.system && f.id !== id && !f.id.startsWith(`${id}/`) && f.id !== here);
}

/** What a folder holds, folders and files all the way down. */
export function holds(folders: LibraryFolder[], assets: LibraryAsset[], id: string): { files: number; folders: number } {
  return { files: countIn(folders, assets, id), folders: folders.filter((f) => f.id.startsWith(`${id}/`)).length };
}

// ── listing ────────────────────────────────────────────────────────────────────

export type SortName = "new" | "old" | "name" | "used";
export const SORTS: Record<SortName, string> = { new: "Newest first", old: "Oldest first", name: "Name", used: "Most used" };
export type KindFilter = "all" | "video" | "image" | "doc" | "sound";

export interface ListContext {
  mode: "pick" | "page";
  folder: string;
  q: string;
  /** The page's kind filter. */
  kind?: KindFilter;
  sort?: SortName;
  fit: Fit;
  /** Show everything, not only what fits (the control's "Show everything"). */
  all?: boolean;
}

export function fits(c: ListContext, a: LibraryAsset): boolean {
  return c.mode !== "pick" || c.fit.test(a);
}

function matches(folders: LibraryFolder[], a: LibraryAsset, q: string): boolean {
  const words = [a.name, kindName(a), folderName(folders, a.folder_id), a.source === "flashkut" ? "flashkut" : "", ...a.uses.map((u) => u.label)];
  return words.join(" ").toLowerCase().includes(q);
}

/** The files a place shows, newest uploads first, then by the chosen order. */
export function listFiles(
  folders: LibraryFolder[],
  assets: LibraryAsset[],
  c: ListContext,
  firstIds: ReadonlySet<string> = new Set(),
): LibraryAsset[] {
  const q = c.q.trim().toLowerCase();
  let xs = assets.filter((a) => (q ? matches(folders, a, q) : c.folder === ALL ? inAll(a) : a.folder_id === c.folder));
  if (c.mode === "page" && c.kind && c.kind !== "all") {
    xs = xs.filter((a) => (c.kind === "doc" ? a.kind === "pdf" || a.kind === "doc" : a.kind === c.kind));
  }
  if (c.mode === "pick" && !c.all) xs = xs.filter(c.fit.test);
  const by: Record<SortName, (x: LibraryAsset, y: LibraryAsset) => number> = {
    new: (x, y) => y.created_at - x.created_at,
    old: (x, y) => x.created_at - y.created_at,
    name: (x, y) => x.name.localeCompare(y.name),
    used: (x, y) => usedTotal(y.uses) - usedTotal(x.uses) || y.created_at - x.created_at,
  };
  const order = by[c.sort ?? "new"];
  // Why first: what was just uploaded is what the person came to use.
  return xs.toSorted((x, y) => Number(firstIds.has(y.id)) - Number(firstIds.has(x.id)) || order(x, y));
}

/**
 * Every file in a folder and the folders inside it: the number its row and card say on the Assets page and in every
 * window the control opens, whatever that place takes. What fits a place narrows the list, never this count.
 */
export function countIn(folders: LibraryFolder[], assets: LibraryAsset[], id: string): number {
  return assets.filter((a) => (id === ALL ? inAll(a) : isUnder(folders, a, id))).length;
}

/** Those same files, newest first with what was just uploaded before them: what a folder's page shows on a phone. */
export function filesUnder(folders: LibraryFolder[], assets: LibraryAsset[], id: string, firstIds: ReadonlySet<string> = new Set()): LibraryAsset[] {
  return assets
    .filter((a) => (id === ALL ? inAll(a) : isUnder(folders, a, id)))
    .toSorted((x, y) => Number(firstIds.has(y.id)) - Number(firstIds.has(x.id)) || y.created_at - x.created_at);
}

/** Does anything in this folder fit the place the control was opened for? A folder with nothing that fits is dimmed. */
export function fitsIn(folders: LibraryFolder[], assets: LibraryAsset[], c: ListContext, id: string): boolean {
  return assets.some((a) => (id === ALL ? inAll(a) : isUnder(folders, a, id)) && fits(c, a));
}

const filesWords = (n: number) => `${n} file${n === 1 ? "" : "s"}`;

/** "12 files", or "12 of 19 files" when what fits the place, or the kind picked, leaves some out. */
export function listedLine(folders: LibraryFolder[], assets: LibraryAsset[], c: ListContext, listed: number): string {
  const all = listFiles(folders, assets, { ...c, all: true, kind: "all" }).length;
  return listed < all ? `${listed} of ${filesWords(all)}` : filesWords(listed);
}

/** Which rail groups hold a match for a search: a closed one opens while it is searched. */
export function groupHits(folders: LibraryFolder[], assets: LibraryAsset[], q: string): Record<"Folders" | ProductGroup, boolean> {
  const s = q.trim().toLowerCase();
  const none = { Folders: false, Flashkut: false, Flashroom: false };
  if (!s) return none;
  const hit = { ...none };
  for (const a of assets) if (matches(folders, a, s)) hit[groupOfAsset(a) ?? "Folders"] = true;
  for (const f of folders) {
    const g = f.system ? groupOfFolder(f) : "Folders";
    if (g && f.name.toLowerCase().includes(s)) hit[g] = true;
  }
  return hit;
}

export function folderMatches(folders: LibraryFolder[], q: string): LibraryFolder[] {
  const s = q.trim().toLowerCase();
  return s ? folders.filter((f) => f.name.toLowerCase().includes(s)) : [];
}

// ── the phone's kinds and tile lines ───────────────────────────────────────────

export type KindGroup = "all" | "video" | "image" | "file";

/** The phone's switch: three kinds, and everything that is not a picture or a video under "Files". */
export const KIND_GROUPS: ReadonlyArray<{ value: KindGroup; label: string }> = [
  { value: "all", label: "Everything" },
  { value: "video", label: "Videos" },
  { value: "image", label: "Pictures" },
  { value: "file", label: "Files" },
];

export const kindGroupOf = (a: Pick<LibraryAsset, "kind">): Exclude<KindGroup, "all"> => (a.kind === "video" ? "video" : a.kind === "image" ? "image" : "file");

/** The kinds worth a switch: only when a place holds six files or more of two kinds or more, and only the kinds it holds. */
export function kindChoices(files: ReadonlyArray<Pick<LibraryAsset, "kind">>): typeof KIND_GROUPS {
  const have = new Set(files.map(kindGroupOf));
  return have.size < 2 || files.length < 6 ? [] : KIND_GROUPS.filter((k) => k.value === "all" || have.has(k.value as Exclude<KindGroup, "all">));
}

/** What a tile says under a file's name on a phone: who it came from for a note or a guest's file, else where it is used. */
export function fileLine(a: Pick<LibraryAsset, "source" | "webinar_id" | "from_label" | "uses">): string {
  if (a.source === "room") return !a.webinar_id && a.from_label ? a.from_label : "Replay";
  return usedLine(a.uses);
}

// ── the grid of true shapes ────────────────────────────────────────────────────

/** A file's width over its height, from what is known of it. */
export function aspectOf(a: Pick<LibraryAsset, "kind" | "width" | "height">): number {
  if (a.kind === "pdf" || a.kind === "doc") return 0.86;
  if (a.kind === "sound") return 1.5;
  if (a.width && a.height) {
    const r = a.width / a.height;
    if (r > 1.15) return 16 / 9;
    if (r < 1 / 1.15) return 0.8;
    return 1;
  }
  return a.kind === "video" ? 16 / 9 : 1;
}

/**
 * Rows of true shapes, each row the full width at about the same height; the last row keeps the height it asks for.
 * Returns each tile's width, in the order given.
 */
export function justifyRows(aspects: number[], width: number, height: number, gap = 14): number[] {
  const out = aspects.map((ar) => Math.floor(ar * height));
  if (!width || !aspects.length) return out;
  let row: number[] = [];
  let sum = 0;
  const place = (idx: number[], h: number) => {
    for (const i of idx) out[i] = Math.floor(aspects[i] * h);
  };
  aspects.forEach((ar, i) => {
    row.push(i);
    sum += ar;
    const h = (width - gap * (row.length - 1)) / sum;
    if (h > height) return;
    const without = row.length > 1 ? (width - gap * (row.length - 2)) / (sum - ar) : Number.POSITIVE_INFINITY;
    if (row.length > 1 && Math.abs(without - height) < Math.abs(h - height) && without < height * 1.45) {
      const last = row.pop() as number;
      place(row, without - 0.5);
      row = [last];
      sum = aspects[last];
    } else {
      place(row, h - 0.5);
      row = [];
      sum = 0;
    }
  });
  if (row.length) place(row, height);
  return out;
}
